import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private _settings: any;
  private _initialized = false;
  private readonly STORAGE_KEY = 'public_installation_settings';
  private _storageInitialized = false;

  constructor(
    private http: HttpClient,
    private storage: StorageService
  ) {
    // Initialize storage as soon as the service is created
    this.initStorage();
  }

  private async initStorage(): Promise<void> {
    try {
      await this.storage.init();
      this._storageInitialized = true;
    } catch (error) {
      console.error('Failed to initialize storage:', error);
    }
  }

  async loadConfig(): Promise<void> {
    if (this._initialized) {
      return;
    }

    try {
      // Try to load from file first
      this._settings = await firstValueFrom(
        this.http.get('assets/public_installation.json')
      );
      
      // Ensure storage is initialized before setting
      if (!this._storageInitialized) {
        await this.initStorage();
      }
      
      // Cache the settings
      if (this._storageInitialized) {
        await this.storage.set(this.STORAGE_KEY, this._settings);
      }
      
      this._initialized = true;
    } catch (error) {
      console.warn('Failed to load from file, trying storage:', error);
      
      // Ensure storage is initialized before getting
      if (!this._storageInitialized) {
        await this.initStorage();
      }
      
      // Only try to get from storage if it's initialized
      if (this._storageInitialized) {
        try {
          this._settings = await this.storage.get(this.STORAGE_KEY);
          if (this._settings) {
            this._initialized = true;
          }
        } catch (storageError) {
          console.error('Failed to load from storage:', storageError);
        }
      }
      
      // If we still don't have settings, initialize with empty object
      if (!this._settings) {
        this._settings = {};
        console.error('Failed to load configuration from any source');
      }
    }
  }

  getSettings(): any {
    if (!this._initialized) {
      console.warn('Attempting to get settings before initialization');
    }
    return this._settings || {};
  }

  get settings(): any {
    return this.getSettings();
  }

  async getConfig(): Promise<any> {
    if (!this._initialized) {
      await this.loadConfig();
    }
    return this.getSettings();
  }

  getAppName(): string {
    return this._settings?.citobs_application?.serviceinfo?.service_start_display_name || 'CitObs App';
  }

  getAppDescription(): string {
    return this._settings?.citobs_application?.serviceinfo?.service_start_display_infotext || '';
  }

  async clearCache(): Promise<void> {
    if (this._storageInitialized) {
      await this.storage.delete(this.STORAGE_KEY);
    }
    this._settings = null;
    this._initialized = false;
  }

  hasSettings(): boolean {
    return !!this._settings;
  }

  isInitialized(): boolean {
    return this._initialized;
  }
}
